// /admin/devices — FleetSpoke super admin (D7/D8): add devices to FleetSpoke stock (Available), allocate them to a
// customer (Assigned, billable from allocation), return / mark faulty / retire; each device's movement history in
// its Logs dialog; the gateway fleet with its health summary; the device type catalogue (any manufacturer, one of
// four fixed categories). The tab is in the URL (?tab=…), and the Assigned tab's account filter too
// (?account=<company code>).
import HistoryIcon from '@mui/icons-material/History';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Loading, LoadError, Page } from '../../components/Page';
import {
  type DeviceRow,
  type DeviceType,
  useAddDevices,
  useCompanies,
  useDeleteDeviceType,
  useDeviceMovements,
  useDevices,
  useDeviceTypes,
  useGatewayHealth,
  useMoveDevice,
  useSaveDeviceType,
  useSetDeviceType,
} from '../../data/admin';
import {
  connectsToTraccar,
  DEVICE_CATEGORIES,
  type DeviceCategory,
  deviceTypeName,
  parseIdentifiers,
} from '../../lib/devices';
import { isoMinuteIn, relativeTime } from '../../lib/time';

const TABS = ['available', 'assigned', 'gateways', 'types'] as const;
type TabKey = (typeof TABS)[number];
// Links from before the 2026-09-15 tab rename land on the tab that replaced them (?tab=movements → default)
const RENAMED: Record<string, TabKey> = {
  fleetspoke: 'available',
  devices: 'assigned',
  health: 'gateways',
};

const errorCode = (e: unknown) => (e as { code?: string } | null)?.code;

function AddDevicesDialog({
  platformId,
  types,
  onClose,
}: {
  platformId: string;
  types: DeviceType[];
  onClose(): void;
}) {
  const { t } = useTranslation();
  const add = useAddDevices();
  const active = types.filter((dt) => dt.is_active);
  const [typeId, setTypeId] = useState(active[0]?.id ?? '');
  const [text, setText] = useState('');
  const [sim, setSim] = useState('');
  const deviceType = active.find((dt) => dt.id === typeId);
  const category: DeviceCategory = deviceType?.category ?? 'ble_beacon';
  const [valid, invalid] = parseIdentifiers(text, category);
  // MUI Select needs its options as a flat list: a subheader per category, then that category's types
  const options = DEVICE_CATEGORIES.flatMap((c) => {
    const inCategory = active.filter((dt) => dt.category === c);
    return inCategory.length
      ? [
          <ListSubheader key={`h-${c}`}>{t(`admin.type_${c}`)}</ListSubheader>,
          ...inCategory.map((dt) => (
            <MenuItem key={dt.id} value={dt.id}>
              {deviceTypeName(dt)}
            </MenuItem>
          )),
        ]
      : [];
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('admin.add_devices')}</DialogTitle>
      <DialogContent dividers>
        {!active.length ? (
          <Alert severity="info">{t('admin.dt_none')}</Alert>
        ) : (
          <Stack spacing={2}>
            <TextField
              select
              label={t('admin.device_type')}
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              helperText={t(`admin.type_${category}`)}
            >
              {options}
            </TextField>
            <TextField
              label={t('admin.identifiers')}
              helperText={
                connectsToTraccar(category)
                  ? t('admin.identifiers_imei_help')
                  : t('admin.identifiers_ble_help')
              }
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={4}
            />
            {connectsToTraccar(category) ? (
              <TextField
                label={`${t('admin.sim_iccid')} (${t('common.optional')})`}
                value={sim}
                onChange={(e) => setSim(e.target.value)}
              />
            ) : null}
            <Typography variant="body2">
              {t('admin.identifiers_count', { valid: valid.length, invalid: invalid.length })}
            </Typography>
            {invalid.length ? (
              <Alert severity="warning">{invalid.slice(0, 5).join(', ')}</Alert>
            ) : null}
            {add.error ? (
              <Alert severity="error">
                {errorCode(add.error) === '23505'
                  ? t('admin.identifier_exists')
                  : (add.error as Error).message}
              </Alert>
            ) : null}
            {add.isSuccess ? (
              <Alert severity="success">{t('admin.devices_added', { count: add.data })}</Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button
          variant="contained"
          disabled={!deviceType || !valid.length || invalid.length > 0 || add.isPending}
          onClick={() =>
            deviceType &&
            add.mutate({
              platformId,
              deviceType,
              identifiers: valid,
              sim: sim.trim() || null,
            })
          }
        >
          {t('admin.add_to_stock')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MoveDialog({
  device,
  types,
  onClose,
}: {
  device: DeviceRow;
  types: DeviceType[];
  onClose(): void;
}) {
  const { t } = useTranslation();
  const companies = useCompanies();
  const move = useMoveDevice();
  const setType = useSetDeviceType();
  const platform = companies.data?.find((c) => c.kind === 'platform');
  const customers = (companies.data ?? []).filter((c) => c.kind === 'customer' && c.is_active);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [typeId, setTypeId] = useState(device.device_type?.id ?? '');
  const sameCategory = types.filter(
    (dt) => dt.category === device.type && (dt.is_active || dt.id === typeId),
  );
  // A retired device goes back to stock (or faulty) before it can be allocated again
  const canAllocate = device.status === 'in_stock' || device.status === 'faulty';
  const go = (toCompanyId: string, toStatus?: 'in_stock' | 'faulty' | 'retired') =>
    move.mutate(
      {
        kind: device.kind,
        id: device.id,
        toCompanyId,
        toStatus,
        reason: reason.trim() || undefined,
      },
      { onSuccess: onClose },
    );
  const changeType = (id: string) => {
    const previous = typeId;
    setTypeId(id);
    setType.mutate(
      { kind: device.kind, id: device.id, deviceTypeId: id },
      { onError: () => setTypeId(previous) },
    );
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ wordBreak: 'break-all' }}>{device.identifier}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {sameCategory.length ? (
            <TextField
              select
              label={t('admin.device_type')}
              value={typeId}
              onChange={(e) => changeType(e.target.value)}
              disabled={setType.isPending}
              helperText={setType.isSuccess ? t('common.saved') : t(`admin.type_${device.type}`)}
            >
              {sameCategory.map((dt) => (
                <MenuItem key={dt.id} value={dt.id}>
                  {deviceTypeName(dt)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {setType.error ? (
            <Alert severity="error">{(setType.error as Error).message}</Alert>
          ) : null}
          {device.status === 'retired' ? (
            <Alert severity="info">{t('admin.retired_hint')}</Alert>
          ) : null}
          {canAllocate ? (
            <TextField
              select
              label={t('admin.allocate_to')}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            >
              {customers.map((c) => (
                <MenuItem key={c.company_id} value={c.company_id}>
                  {c.name} ({c.code})
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            label={`${t('admin.reason')} (${t('common.optional')})`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {move.error ? <Alert severity="error">{(move.error as Error).message}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {canAllocate ? (
          <Button
            variant="contained"
            disabled={!target || move.isPending}
            onClick={() => go(target)}
          >
            {t('admin.allocate')}
          </Button>
        ) : null}
        {platform && device.status !== 'in_stock' ? (
          <Button disabled={move.isPending} onClick={() => go(platform.company_id, 'in_stock')}>
            {t('admin.return_to_stock')}
          </Button>
        ) : null}
        {platform && device.status !== 'faulty' ? (
          <Button
            color="warning"
            disabled={move.isPending}
            onClick={() => go(platform.company_id, 'faulty')}
          >
            {t('admin.mark_faulty')}
          </Button>
        ) : null}
        {platform && device.status !== 'retired' ? (
          <Button
            color="error"
            disabled={move.isPending}
            onClick={() => go(platform.company_id, 'retired')}
          >
            {t('admin.retire')}
          </Button>
        ) : null}
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeviceTypeDialog({
  value,
  inUse,
  manufacturers,
  onClose,
}: {
  value: DeviceType | null;
  inUse: number;
  manufacturers: string[];
  onClose(): void;
}) {
  const { t } = useTranslation();
  const save = useSaveDeviceType();
  const remove = useDeleteDeviceType();
  const [category, setCategory] = useState<DeviceCategory>(value?.category ?? 'ble_beacon');
  const [manufacturer, setManufacturer] = useState(value?.manufacturer ?? '');
  const [model, setModel] = useState(value?.model ?? '');
  const [protocol, setProtocol] = useState(value?.traccar_protocol ?? '');
  const [note, setNote] = useState(value?.note ?? '');
  const [active, setActive] = useState(value?.is_active ?? true);
  const error = save.error ?? remove.error;
  const message = !error
    ? null
    : ((
        {
          '23505': t('admin.dt_exists'),
          '23503': t('admin.dt_in_use', { count: inUse }),
          '55000': t('admin.dt_category_locked'),
          '23514': t('admin.dt_invalid'),
          '42501': t('common.not_allowed'),
        } as Record<string, string>
      )[errorCode(error) ?? ''] ?? (error as Error).message);
  const submit = () =>
    save.mutate(
      {
        id: value?.id,
        category,
        manufacturer,
        model,
        traccar_protocol: connectsToTraccar(category) ? protocol : null,
        note,
        is_active: active,
      },
      { onSuccess: onClose },
    );
  const busy = save.isPending || remove.isPending;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{value ? t('admin.edit_device_type') : t('admin.add_device_type')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Autocomplete
            freeSolo
            options={manufacturers}
            inputValue={manufacturer}
            onInputChange={(_, v) => setManufacturer(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('admin.dt_manufacturer')}
                required
                autoFocus={!value}
                slotProps={{ htmlInput: { ...params.inputProps, maxLength: 60 } }}
              />
            )}
          />
          <TextField
            label={t('admin.dt_model')}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            required
            slotProps={{ htmlInput: { maxLength: 80 } }}
          />
          <TextField
            select
            label={t('admin.dt_category')}
            value={category}
            onChange={(e) => setCategory(e.target.value as DeviceCategory)}
            disabled={inUse > 0}
            helperText={
              inUse > 0 ? t('admin.dt_in_use', { count: inUse }) : t('admin.dt_category_help')
            }
          >
            {DEVICE_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {t(`admin.type_${c}`)}
              </MenuItem>
            ))}
          </TextField>
          {connectsToTraccar(category) ? (
            <TextField
              label={`${t('admin.dt_protocol')} (${t('common.optional')})`}
              value={protocol}
              onChange={(e) => setProtocol(e.target.value)}
              helperText={t('admin.dt_protocol_help')}
            />
          ) : null}
          <TextField
            label={`${t('admin.dt_note')} (${t('common.optional')})`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          {value ? (
            <FormControlLabel
              control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
              label={active ? t('admin.col_active') : t('admin.dt_archived')}
            />
          ) : null}
          {message ? <Alert severity="error">{message}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {value && inUse === 0 ? (
          <Button
            color="error"
            disabled={busy}
            onClick={() => remove.mutate(value.id, { onSuccess: onClose })}
            sx={{ mr: 'auto' }}
          >
            {t('admin.dt_delete')}
          </Button>
        ) : null}
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={busy || !manufacturer.trim() || !model.trim()}
          onClick={submit}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const STATUS_COLOR = {
  in_stock: 'default',
  allocated: 'success',
  faulty: 'warning',
  retired: 'error',
} as const;

/** A device's allocation / return history, newest first — opened from the device's Logs button. */
function DeviceLogsDialog({
  device,
  timeZone,
  onClose,
}: {
  device: DeviceRow;
  timeZone: string;
  onClose(): void;
}) {
  const { t, i18n } = useTranslation();
  const logs = useDeviceMovements(device.kind, device.id);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t('admin.col_logs')}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {device.identifier}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {logs.isLoading ? (
          <Loading />
        ) : logs.error ? (
          <LoadError error={logs.error} />
        ) : !logs.data?.length ? (
          <Typography color="text.secondary">{t('admin.logs_none')}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.col_when')}</TableCell>
                  <TableCell>{t('admin.col_move')}</TableCell>
                  <TableCell>{t('admin.reason')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.data.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {isoMinuteIn(m.moved_at, timeZone)}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {relativeTime(m.moved_at, i18n.language)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {m.from_company?.code} ({t(`admin.status_${m.from_status}`)}) →{' '}
                      {m.to_company?.code} ({t(`admin.status_${m.to_status}`)})
                    </TableCell>
                    <TableCell>{m.reason ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DevicesTable({
  rows,
  onMove,
  onLogs,
}: {
  rows: DeviceRow[];
  onMove(d: DeviceRow): void;
  onLogs(d: DeviceRow): void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('admin.device_type')}</TableCell>
            <TableCell>{t('admin.identifier')}</TableCell>
            <TableCell>{t('admin.col_status')}</TableCell>
            <TableCell>{t('admin.col_account')}</TableCell>
            <TableCell>{t('admin.col_attached')}</TableCell>
            <TableCell>{t('admin.col_last_seen')}</TableCell>
            <TableCell>{t('admin.col_logs')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((d) => (
            <TableRow key={`${d.kind}-${d.id}`} data-testid={`device-${d.identifier}`}>
              <TableCell>
                {d.device_type ? (
                  <>
                    {deviceTypeName(d.device_type)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t(`admin.type_${d.type}`)}
                    </Typography>
                  </>
                ) : (
                  t(`admin.type_${d.type}`)
                )}
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{d.identifier}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={STATUS_COLOR[d.status]}
                  label={t(`admin.status_${d.status}`)}
                />
              </TableCell>
              <TableCell>{d.company}</TableCell>
              <TableCell>{d.attached ?? '—'}</TableCell>
              <TableCell>
                {relativeTime(d.last_seen_at, i18n.language) ?? t('common.never')}
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  aria-label={t('admin.logs_show')}
                  onClick={() => onLogs(d)}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => onMove(d)}>
                  {t('admin.move')}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function DeviceTypesTab({
  types,
  inUse,
  onEdit,
}: {
  types: DeviceType[];
  inUse: Map<string, number>;
  onEdit(dt: DeviceType): void;
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('admin.dt_intro')}</Alert>
      {!types.length ? <Typography color="text.secondary">{t('admin.dt_none')}</Typography> : null}
      {types.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.dt_manufacturer')}</TableCell>
                <TableCell>{t('admin.dt_model')}</TableCell>
                <TableCell>{t('admin.dt_category')}</TableCell>
                <TableCell>{t('admin.dt_protocol')}</TableCell>
                <TableCell align="right">{t('admin.dt_devices')}</TableCell>
                <TableCell>{t('admin.col_status')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((dt) => (
                <TableRow key={dt.id} data-testid={`device-type-${dt.model}`}>
                  <TableCell>{dt.manufacturer}</TableCell>
                  <TableCell>
                    {dt.model}
                    {dt.note ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {dt.note}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{t(`admin.type_${dt.category}`)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {dt.traccar_protocol ?? '—'}
                  </TableCell>
                  <TableCell align="right">{inUse.get(dt.id) ?? 0}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={dt.is_active ? 'success' : 'default'}
                      label={dt.is_active ? t('admin.col_active') : t('admin.dt_archived')}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => onEdit(dt)}>
                      {t('common.edit')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Stack>
  );
}

export function AdminDevicesPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') ?? '';
  const tab: TabKey = (TABS as readonly string[]).includes(requested)
    ? (requested as TabKey)
    : (RENAMED[requested] ?? 'available');
  const account = params.get('account') ?? '';
  const devices = useDevices();
  const types = useDeviceTypes();
  const companies = useCompanies();
  const health = useGatewayHealth();
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState<DeviceRow | null>(null);
  const [logsOf, setLogsOf] = useState<DeviceRow | null>(null);
  const [editing, setEditing] = useState<DeviceType | 'new' | null>(null);
  const platform = companies.data?.find((c) => c.kind === 'platform');
  const customers = (companies.data ?? []).filter((c) => c.kind === 'customer');
  const timeZone = platform?.timezone ?? 'Asia/Kuala_Lumpur';
  const all = useMemo(() => devices.data ?? [], [devices.data]);
  // Available: everything Fleetspoke holds (in stock, faulty, retired). Assigned: devices allocated to customers.
  const assigned = useMemo(
    () => all.filter((d) => d.company_id !== platform?.company_id),
    [all, platform],
  );
  const rows = useMemo(
    () =>
      tab === 'available'
        ? all.filter((d) => d.company_id === platform?.company_id)
        : account
          ? assigned.filter((d) => d.company === account)
          : assigned,
    [all, assigned, tab, account, platform],
  );
  const inUse = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of all)
      if (d.device_type) m.set(d.device_type.id, (m.get(d.device_type.id) ?? 0) + 1);
    return m;
  }, [all]);
  const manufacturers = useMemo(
    () => [...new Set((types.data ?? []).map((dt) => dt.manufacturer))].sort(),
    [types.data],
  );
  const offline = (health.data ?? []).filter((g) => !g.is_online).length;
  const online = (health.data?.length ?? 0) - offline;

  const update = (change: (p: URLSearchParams) => void) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        change(next);
        return next;
      },
      { replace: true },
    );
  const setTab = (v: TabKey) =>
    update((p) => {
      p.set('tab', v);
      if (v !== 'assigned') p.delete('account');
    });
  const setAccount = (code: string) =>
    update((p) => {
      if (code) p.set('account', code);
      else p.delete('account');
    });

  return (
    <Page
      title={t('admin.devices_title')}
      actions={
        tab === 'types' ? (
          <Button variant="contained" onClick={() => setEditing('new')}>
            {t('admin.add_device_type')}
          </Button>
        ) : platform ? (
          <Button variant="contained" onClick={() => setAdding(true)} disabled={!types.data}>
            {t('admin.add_devices')}
          </Button>
        ) : null
      }
    >
      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => setTab(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        <Tab value="available" label={t('admin.tab_available')} />
        <Tab value="assigned" label={t('admin.tab_assigned')} />
        <Tab value="gateways" label={t('admin.tab_gateway')} />
        <Tab value="types" label={t('admin.tab_types')} />
      </Tabs>

      {tab === 'available' || tab === 'assigned' ? (
        devices.isLoading || companies.isLoading ? (
          <Loading />
        ) : devices.error || companies.error ? (
          <LoadError error={devices.error ?? companies.error} />
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              sx={{ mb: 2 }}
            >
              {tab === 'assigned' ? (
                <TextField
                  select
                  size="small"
                  label={t('admin.filter_account')}
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  sx={{ minWidth: 240 }}
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                >
                  <MenuItem value="">{t('admin.all_accounts')}</MenuItem>
                  {customers.map((c) => (
                    <MenuItem key={c.company_id} value={c.code}>
                      {c.name} ({c.code})
                    </MenuItem>
                  ))}
                  {account && !customers.some((c) => c.code === account) ? (
                    <MenuItem value={account}>{account}</MenuItem>
                  ) : null}
                </TextField>
              ) : null}
              <Typography variant="body2" color="text.secondary" data-testid="device-count">
                {tab === 'assigned' && account
                  ? t('admin.devices_shown', { shown: rows.length, total: assigned.length })
                  : t('admin.devices_total', { count: rows.length })}
              </Typography>
            </Stack>
            <DevicesTable rows={rows} onMove={setMoving} onLogs={setLogsOf} />
          </>
        )
      ) : null}

      {tab === 'gateways' ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.col_account')}</TableCell>
                <TableCell>{t('admin.col_gateway')}</TableCell>
                <TableCell>{t('admin.col_site')}</TableCell>
                <TableCell>{t('admin.col_status')}</TableCell>
                <TableCell>{t('admin.col_last_seen')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(health.data ?? []).map((g) => (
                <TableRow key={g.id}>
                  <TableCell>{g.companies?.code}</TableCell>
                  <TableCell>{g.display_name ?? g.identifier}</TableCell>
                  <TableCell>{g.sites?.name ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={g.is_online ? 'success' : 'error'}
                      label={g.is_online ? t('admin.online') : t('admin.offline')}
                    />
                  </TableCell>
                  <TableCell>
                    {relativeTime(g.last_seen_at, i18n.language) ?? t('common.never')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {health.isLoading ? <Loading /> : null}
          {health.error ? <LoadError error={health.error} /> : null}
          {health.data && !health.data.length ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {t('admin.no_gateways')}
            </Typography>
          ) : null}
          {health.data?.length ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 2 }}
              data-testid="gateway-health"
            >
              <Typography variant="body2" color="text.secondary">
                {t('admin.gateway_health')}
              </Typography>
              <Chip
                size="small"
                color="success"
                label={t('admin.health_online', { count: online })}
              />
              <Chip
                size="small"
                color={offline ? 'error' : 'default'}
                label={t('admin.health_offline', { count: offline })}
              />
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {tab === 'types' ? (
        types.isLoading ? (
          <Loading />
        ) : types.error ? (
          <LoadError error={types.error} />
        ) : (
          <DeviceTypesTab types={types.data ?? []} inUse={inUse} onEdit={setEditing} />
        )
      ) : null}

      {adding && platform ? (
        <AddDevicesDialog
          platformId={platform.company_id}
          types={types.data ?? []}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {moving ? (
        <MoveDialog device={moving} types={types.data ?? []} onClose={() => setMoving(null)} />
      ) : null}
      {logsOf ? (
        <DeviceLogsDialog device={logsOf} timeZone={timeZone} onClose={() => setLogsOf(null)} />
      ) : null}
      {editing ? (
        <DeviceTypeDialog
          value={editing === 'new' ? null : editing}
          inUse={editing === 'new' ? 0 : (inUse.get(editing.id) ?? 0)}
          manufacturers={manufacturers}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Page>
  );
}
